import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wallet,
  AlertCircle,
  CheckCircle,
  DollarSign,
  Users,
  Calendar,
  Edit,
  Eye,
  Search,
  Plus,
  FileText,
  Filter,
  X,
  XCircle,
  CheckSquare,
  Square,
  Upload,
  Trash2,
  Download,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  FileOutput,
  LayoutGrid,
  TrendingUp,
  Layers,
  List,
  LayoutList,
  Shield,
  Tag
} from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { usePayments } from '../../core/contexts/PaymentsContext';
import { useDocuments } from '../../core/contexts/DocumentsContext';
import { useClients } from '../../core/contexts/ClientsContext';
import { useAppData } from '../../core/contexts/AppDataContext';
import {
  formatBrandMoney,
  getBrandDefaultCurrency,
  getBrandCurrencySuffix,
  getBrandLocale,
  getBrandDatePickerLocale,
  getBrandPriceTierThresholds,
} from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { invoiceService } from '../../shared/utils/serviceProvider';
// Note: paiements liste = agrégé depuis les factures (DocumentsContext); l'édition inline met à jour la facture.
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { useSettings } from '../../core/contexts/SettingsContext';
import { printListPdfWithInvoiceTemplate } from './utils/templateListPdfExport';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../core/styles/global-dropdowns.css';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import FacturationListPageLayout, {
  facturationTableHeadClass,
  facturationTableScrollClass,
} from './components/FacturationListPageLayout';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import useFacturationModalBodyLock from './utils/useFacturationModalBodyLock';
import { useFacturationConfig } from './useFacturationConfig';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';

registerLocale('ar', arLocale);
registerLocale('ar-SA', arLocale);

// Inline edit cell for Payments - MUST stay outside Payments
function PaymentEditableCell({
  recordId, field, value, className = 'text-white font-semibold',
  isEditing, editValue, onEditValueChange, onStartEdit, onSave, onCancel,
  editTitle,
  readOnly = true,
  displayValue,
}) {
  const inputRef = useRef(null);
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    if (readOnly || !isEditing || !inputRef.current) return;
    const el = inputRef.current;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      el.focus();
      const len = el.value.length;
      try { el.setSelectionRange(len, len); } catch {}
    });
  }, [isEditing, readOnly]);

  if (readOnly) {
    return <span className={className}>{(displayValue ?? value) || '-'}</span>;
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef} type="text" value={editValue}
        onChange={(e) => onEditValueChange(e.target.value)}
        onBlur={(e) => {
          if (skipBlurSaveRef.current) { skipBlurSaveRef.current = false; return; }
          onSave(recordId, field, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); skipBlurSaveRef.current = true; onSave(recordId, field, e.currentTarget.value); }
          else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
        onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
        className={`${className} bg-transparent border-none outline-none focus:outline-none w-full px-0`}
        style={{ caretColor: 'white' }} autoComplete="off" spellCheck={false}
      />
    );
  }

  return (
    <span role="button" tabIndex={0}
      onClick={() => onStartEdit(recordId, field, value)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStartEdit(recordId, field, value); } }}
      className={`${className} cursor-pointer`} title={editTitle}
    >{value || '-'}</span>
  );
}

const Payments = () => {
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.facturation.pages.paymentsPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const amountTiers = useMemo(() => {
    const { lowMax, highMin } = getBrandPriceTierThresholds();
    return {
      top: isSaudiBrand() ? highMin * 10 : 100000,
      high: isSaudiBrand() ? highMin : 50000,
      mid: isSaudiBrand() ? lowMax : 10000,
    };
  }, []);
  const paymentMethodOptions = useMemo(
    () => [
      { value: 'cash', label: p('method_cash') },
      { value: 'check', label: p('method_check') },
      { value: 'transfer', label: p('method_transfer') },
      { value: 'sarie', label: p('method_sarie') },
      { value: 'mada', label: p('method_mada') },
      { value: 'card', label: p('method_card') },
    ],
    [t],
  );
  const {
    payments,
    loading,
    getPaymentStats,
    markInstallmentAsPaid,
    createPayment,
    updatePayment,
    deletePayment,
    refreshData,
  } = usePayments();
  const { documents } = useDocuments();
  const { clients, suppliers } = useClients();
  const { settings } = useSettings();
  const facturationConfig = useFacturationConfig();
  const { sales, purchases, getOrders } = useAppData();

  // Export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);

  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // Advanced options toggle
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // View mode: table or cards

  const defaultPaymentMethod = facturationConfig.defaultPaymentMethod || 'cash';

  // View modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  // Inline editing states
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Main view mode for Options Avancées tabs
  const [mainViewMode, setMainViewMode] = useState('all_payments');

  // Quick filter (replaces old statusFilter in advanced options)
  const [quickFilter, setQuickFilter] = useState('all');

  // Distribution filter
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);

  // Status filter dropdown
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);

  // Date range picker
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);

  // Clickable status dropdown (portal)

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);

  // Existing state
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  useFacturationModalBodyLock(showViewModal, showAddPaymentModal, showPaymentModal);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({
    invoiceId: '',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    description: '',
    method: defaultPaymentMethod,
  });

  // Load invoices separately
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);

  useEffect(() => {
    const loadInvoices = async () => {
      try {
        setInvoicesLoading(true);
        const invoicesData = await invoiceService.getInvoices();
        setInvoices(invoicesData || []);
      } catch (error) {
        console.error('Error loading invoices:', error);
        setInvoices([]);
      } finally {
        setInvoicesLoading(false);
      }
    };
    loadInvoices();
  }, []);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Column header filters ─────────────────────────────────────────────────
  const columnFilterRules = useMemo(() => [
    textColumnRule('invoiceNumber', (row) => row.invoiceNumber),
    textColumnRule('client', (row) => row.client),
    textColumnRule('totalAmount', (row) => String(row.totalAmount ?? '')),
    textColumnRule('amountPaid', (row) => String(row.amountPaid ?? '')),
    exactColumnRule('status', (row) => row.status),
    textColumnRule('nextDueDate', (row) => row.nextDueDate),
  ], []);

  // ─── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const pendingPayments = payments.filter(p => p.status === 'pending').length;
    const partialPayments = payments.filter(p => p.status === 'partial').length;
    const completedPayments = payments.filter(p => p.status === 'paid').length;
    const totalReceivable = payments.reduce((sum, p) => sum + (p.totalAmount || 0), 0);
    return { pendingPayments, partialPayments, completedPayments, totalReceivable: totalReceivable || 0 };
  }, [payments]);

  // ─── Integrated Payments Stats ─────────────────────────────────────────────
  const integratedPaymentsStats = useMemo(() => {
    try {
      let totalSalesPayments = 0, pendingSalesPayments = 0;
      let totalPurchasePayments = 0, pendingPurchasePayments = 0;
      let totalOrderPayments = 0, pendingOrderPayments = 0;
      if (sales && Array.isArray(sales)) {
        sales.forEach(sale => {
          const amount = parseFloat(sale.total) || 0;
          totalSalesPayments += amount;
          if (sale.paymentStatus === 'pending') pendingSalesPayments += amount;
        });
      }
      if (purchases && Array.isArray(purchases)) {
        purchases.forEach(purchase => {
          const amount = parseFloat(purchase.total) || 0;
          totalPurchasePayments += amount;
          if (purchase.paymentStatus === 'pending') pendingPurchasePayments += amount;
        });
      }
      const orders = getOrders ? getOrders() : [];
      if (Array.isArray(orders)) {
        orders.forEach(order => {
          const amount = parseFloat(order.total) || 0;
          totalOrderPayments += amount;
          if (order.paymentStatus === 'pending') pendingOrderPayments += amount;
        });
      }
      return {
        totalSalesPayments, pendingSalesPayments, totalPurchasePayments, pendingPurchasePayments,
        totalOrderPayments, pendingOrderPayments,
        totalIntegratedPayments: totalSalesPayments + totalPurchasePayments + totalOrderPayments,
        totalPendingPayments: pendingSalesPayments + pendingPurchasePayments + pendingOrderPayments
      };
    } catch (error) {
      return { totalSalesPayments: 0, pendingSalesPayments: 0, totalPurchasePayments: 0, pendingPurchasePayments: 0, totalOrderPayments: 0, pendingOrderPayments: 0, totalIntegratedPayments: 0, totalPendingPayments: 0 };
    }
  }, [sales, purchases, getOrders]);

  const getPaymentHistory = (type = 'all') => {
    const history = [];
    try {
      if ((type === 'all' || type === 'sales') && sales && Array.isArray(sales)) {
        sales.forEach(sale => {
          if (sale.paymentStatus === 'completed' || sale.paymentStatus === 'paid') {
            history.push({ id: `sale-${sale.id}`, type: 'vente', date: sale.date, amount: parseFloat(sale.total) || 0, client: sale.customerName || p('unknown_client'), clientId: sale.customerId, reference: sale.invoiceNumber || `INV-${sale.id}`, status: 'paid', paymentMethod: sale.paymentMethod || 'unspecified' });
          }
        });
      }
      if ((type === 'all' || type === 'purchases') && purchases && Array.isArray(purchases)) {
        purchases.forEach(purchase => {
          if (purchase.paymentStatus === 'completed' || purchase.paymentStatus === 'paid') {
            history.push({ id: `purchase-${purchase.id}`, type: 'achat', date: purchase.date, amount: parseFloat(purchase.total) || 0, client: purchase.supplierName || p('unknown_supplier'), clientId: purchase.supplierId, reference: purchase.purchaseNumber || `PO-${purchase.id}`, status: 'paid', paymentMethod: purchase.paymentMethod || 'unspecified' });
          }
        });
      }
      if (type === 'all' || type === 'orders') {
        const orders = getOrders ? getOrders() : [];
        if (Array.isArray(orders)) {
          orders.forEach(order => {
            if (order.paymentStatus === 'completed' || order.paymentStatus === 'paid') {
              history.push({ id: `order-${order.id}`, type: 'commande', date: order.date || order.orderDate, amount: parseFloat(order.total) || 0, client: order.customerName || order.supplierName || p('unknown_client'), clientId: order.customerId || order.supplierId, reference: order.orderNumber || `CMD-${order.id}`, status: 'paid', paymentMethod: order.paymentMethod || 'unspecified' });
            }
          });
        }
      }
      return history.sort((a, b) => new Date(b.date) - new Date(a.date));
    } catch (error) { return []; }
  };

  // ─── Filtered + sorted payments ────────────────────────────────────────────
  const filteredPayments = useMemo(() => {
    const base = payments.filter(payment => {
      const matchesSearch = !searchTerm ||
        payment.client?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;

      if (statusFilter !== 'all' && payment.status !== statusFilter) return false;

      if (quickFilter === 'pending' && payment.status !== 'pending') return false;
      if (quickFilter === 'partial' && payment.status !== 'partial') return false;
      if (quickFilter === 'paid' && payment.status !== 'paid') return false;
      if (quickFilter === 'late' && payment.status !== 'late') return false;

      if (activeDistributionFilter) {
        if (mainViewMode === 'par_montant') {
          const amount = payment.totalAmount || 0;
          if (activeDistributionFilter === 'gt_100k' && amount < amountTiers.top) return false;
          if (activeDistributionFilter === '50k_100k' && (amount < amountTiers.high || amount >= amountTiers.top)) return false;
          if (activeDistributionFilter === '10k_50k' && (amount < amountTiers.mid || amount >= amountTiers.high)) return false;
          if (activeDistributionFilter === 'lt_10k' && amount >= amountTiers.mid) return false;
        } else if (mainViewMode === 'par_client') {
          if (payment.client !== activeDistributionFilter) return false;
        } else {
          if (activeDistributionFilter === 'pending' && payment.status !== 'pending') return false;
          if (activeDistributionFilter === 'partial' && payment.status !== 'partial') return false;
          if (activeDistributionFilter === 'paid' && payment.status !== 'paid') return false;
          if (activeDistributionFilter === 'late' && payment.status !== 'late') return false;
        }
      }

      if (dateRange.start || dateRange.end) {
        const paymentDate = payment.nextDueDate ? new Date(payment.nextDueDate) : null;
        if (paymentDate) {
          if (dateRange.start) {
            const startOfDay = new Date(dateRange.start); startOfDay.setHours(0, 0, 0, 0);
            if (paymentDate < startOfDay) return false;
          }
          if (dateRange.end) {
            const endOfDay = new Date(dateRange.end); endOfDay.setHours(23, 59, 59, 999);
            if (paymentDate > endOfDay) return false;
          }
        }
      }

      return true;
    });
    return applyColumnFilters(base, filters, columnFilterRules);
  }, [payments, searchTerm, statusFilter, quickFilter, activeDistributionFilter, dateRange, filters, columnFilterRules, amountTiers, mainViewMode]);


  // ─── Inline editing functions ──────────────────────────────────────────────
  const startEditing = (recordId, field, currentValue) => {
    setEditingCell({ recordId, field });
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveInlineEdit = async (recordId, field, rawValue) => {
    const record = payments.find(p => p.id === recordId);
    if (!record) { cancelEditing(); return; }

    const trimmed = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    const originalValue = record[field];
    const same =
      trimmed === originalValue ||
      (typeof trimmed === 'string' && trimmed === '' && (originalValue == null || originalValue === ''));
    if (same) {
      cancelEditing();
      return;
    }

    try {
      const nextVal = field === 'totalAmount' || field === 'amountPaid' || field === 'remainingAmount'
        ? Number(String(trimmed).replace(',', '.'))
        : trimmed;
      await updatePayment(recordId, { [field]: nextVal });
      cancelEditing();
    } catch (error) {
      console.error('Error saving inline edit:', error);
      cancelEditing();
    }
  };

  // ─── Distribution data ─────────────────────────────────────────────────────
  const getStatusLabel = (status) => {
    const map = {
      pending: p('status_pending'),
      partial: p('status_partial'),
      paid: p('status_paid'),
      late: p('status_late'),
    };
    return map[status] || p('status_pending');
  };

  const getPaymentMethodLabel = (method) => {
    const map = {
      cash: p('method_cash'),
      check: p('method_check'),
      transfer: p('method_transfer'),
      sarie: p('method_sarie'),
      mada: p('method_mada'),
      card: p('method_card'),
    };
    return map[method] || method;
  };

  const getExportHeaders = () => [
    { key: 'client', label: p('export_col_client') },
    { key: 'invoiceNumber', label: p('export_col_invoice') },
    { key: 'totalAmount', label: p('export_col_total') },
    { key: 'amountPaid', label: p('export_col_paid') },
    { key: 'remainingAmount', label: p('export_col_remaining') },
    { key: 'status', label: p('export_col_status') },
    { key: 'nextDueDate', label: p('export_col_due') },
  ];

  const mainTabs = [
    { id: 'all_payments', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: Shield },
    { id: 'par_client', name: p('tab_by_client'), icon: Users },
    { id: 'par_montant', name: p('tab_by_amount'), icon: TrendingUp }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'pending', name: p('filter_pending'), icon: AlertCircle },
    { id: 'partial', name: p('filter_partial'), icon: DollarSign },
    { id: 'paid', name: p('filter_paid'), icon: CheckCircle },
    { id: 'late', name: p('filter_late'), icon: AlertCircle }
  ];

  const paymentDistribution = useMemo(() => ({
    all_payments: {
      title: p('overview_title'),
      data: [
        { id: 'pending', name: p('dist_pending'), count: stats.pendingPayments, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { id: 'partial', name: p('dist_partial'), count: stats.partialPayments, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'paid', name: p('dist_paid'), count: stats.completedPayments, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'late', name: p('dist_late'), count: payments.filter(pay => pay.status === 'late').length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { id: 'pending', name: p('dist_pending'), count: stats.pendingPayments, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { id: 'partial', name: p('dist_partial'), count: stats.partialPayments, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'paid', name: p('dist_paid'), count: stats.completedPayments, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'late', name: p('dist_late'), count: payments.filter(pay => pay.status === 'late').length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    },
    par_client: {
      title: p('dist_by_client_title'),
      data: (() => {
        const clientMap = {};
        payments.forEach(pay => { const c = pay.client || p('unknown_client'); if (!clientMap[c]) clientMap[c] = 0; clientMap[c]++; });
        return Object.entries(clientMap).slice(0, 4).map(([name, count], i) => ({
          id: name,
          name, count,
          color: ['bg-gradient-to-r from-blue-500 to-blue-600', 'bg-gradient-to-r from-green-500 to-green-600', 'bg-gradient-to-r from-purple-500 to-purple-600', 'bg-gradient-to-r from-orange-500 to-orange-600'][i] || 'bg-gradient-to-r from-gray-500 to-gray-600'
        }));
      })()
    },
    par_montant: {
      title: p('dist_by_amount_title'),
      data: [
        { id: 'gt_100k', name: p('dist_amount_gt_100k'), count: payments.filter(pay => (pay.totalAmount || 0) >= amountTiers.top).length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: '50k_100k', name: p('dist_amount_50k_100k'), count: payments.filter(pay => { const a = pay.totalAmount || 0; return a >= amountTiers.high && a < amountTiers.top; }).length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: '10k_50k', name: p('dist_amount_10k_50k'), count: payments.filter(pay => { const a = pay.totalAmount || 0; return a >= amountTiers.mid && a < amountTiers.high; }).length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { id: 'lt_10k', name: p('dist_amount_lt_10k'), count: payments.filter(pay => (pay.totalAmount || 0) < amountTiers.mid).length, color: 'bg-gradient-to-r from-gray-500 to-gray-600' }
      ]
    }
  }), [payments, stats, t, amountTiers]);

  const distributionInfo = paymentDistribution[mainViewMode] || paymentDistribution.all_payments;

  // ─── Payment status options ─────────────────────────────────────────────────
  const paymentStatusOptions = [
    { value: 'pending', label: p('status_pending'), icon: AlertCircle, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: 'partial', label: p('status_partial'), icon: DollarSign, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'paid', label: p('status_paid'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'late', label: p('status_late'), icon: AlertCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ];

  // ─── StatusBadge ───────────────────────────────────────────────────────────
  const StatusBadge = ({ status }) => {
    const config = paymentStatusOptions.find(s => s.value === status) || paymentStatusOptions[0];
    const { label, icon: Icon, style } = config;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${style}`}>
        <Icon size={12} />{label}
      </span>
    );
  };

  const statusOptions = paymentStatusOptions;

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updatePayment(id, { status: newStatus });
      await refreshData();
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm(p('confirm_delete') || t('common.delete'))) return;
    try {
      await deletePayment(paymentId);
      await refreshData();
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert(p('error_delete'));
    }
  };

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleSelectPayment = (paymentId) => {
    setSelectedPayments(prev => prev.includes(paymentId) ? prev.filter(id => id !== paymentId) : [...prev, paymentId]);
  };

  const {
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredPayments);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  const filteredRecordIds = useMemo(() => filteredPayments.map((r) => r.id), [filteredPayments]);

  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedPayments.includes(id));
  const someRowsSelected = selectedPayments.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedPayments([]);
    } else {
      setSelectedPayments(filteredRecordIds);
    }
  };

  const handleBulkDeletePayments = async () => {
    if (selectedPayments.length === 0) return;
    const n = selectedPayments.length;
    if (!window.confirm(p('bulk_delete_confirm', { count: n }))) return;

    try {
      await Promise.all(selectedPayments.map((pid) => deletePayment(pid)));
        setSelectedPayments([]);
      await refreshData();
    } catch (error) {
      console.error('Bulk delete payments error:', error);
      alert(p('error_delete'));
    }
  };

  const handleQuickAction = (payment, action) => {
    switch (action) {
      case 'view':
        handleViewPaymentDetails(payment);
        break;
      case 'markPaid':
        setPaymentFormData({ invoiceId: payment.invoiceId, amount: payment.remainingAmount.toString(), paymentDate: new Date().toISOString().split('T')[0], description: p('full_payment_desc'), method: defaultPaymentMethod });
        setShowAddPaymentModal(true);
        break;
      case 'addPayment':
        setPaymentFormData({ invoiceId: payment.invoiceId, amount: '', paymentDate: new Date().toISOString().split('T')[0], description: '', method: defaultPaymentMethod });
        setShowAddPaymentModal(true);
        break;
      default:
        break;
    }
  };

  const handleViewPaymentDetails = (payment) => {
    setSelectedPayment(payment);
    setShowPaymentModal(true);
  };

  const handleMarkAsPaid = async (paymentId, installmentId) => {
    try {
      await markInstallmentAsPaid(paymentId, installmentId);
      if (selectedPayment && selectedPayment.id === paymentId) {
        const updatedPayment = payments.find(p => p.id === paymentId);
        if (updatedPayment) setSelectedPayment(updatedPayment);
      }
    } catch (error) {
      console.error('Error marking installment as paid:', error);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      const paymentAmount = parseFloat(paymentFormData.amount) || 0;
      const invoice = documents.find(
        (d) => d.id === paymentFormData.invoiceId && (d.type === 'invoice' || d.type === 'facture')
      );
      const existing = payments.find((pay) => pay.invoiceId === paymentFormData.invoiceId);
      const totalAmount = existing?.totalAmount ?? Number(invoice?.amount ?? invoice?.total ?? 0);
      const amountPaid = (existing?.amountPaid ?? 0) + paymentAmount;
      const remainingAmount = Math.max(0, totalAmount - amountPaid);
      const status = amountPaid >= totalAmount ? 'paid' : amountPaid > 0 ? 'partial' : 'pending';

      if (existing) {
        await updatePayment(existing.id, {
          amountPaid,
          remainingAmount,
          status,
          paymentDate: paymentFormData.paymentDate,
          paymentMethod: paymentFormData.method,
          notes: paymentFormData.description || existing.notes,
        });
      } else {
        await createPayment(paymentFormData.invoiceId, {
          invoiceNumber: invoice?.number,
          client: invoice?.client,
          totalAmount,
          amountPaid,
          remainingAmount,
          status,
          paymentDate: paymentFormData.paymentDate,
          method: paymentFormData.method,
          notes: paymentFormData.description,
          dueDate: invoice?.dueDate || invoice?.due_date || invoice?.date,
        });
      }

      await refreshData();
      setPaymentFormData({ invoiceId: '', amount: '', paymentDate: new Date().toISOString().split('T')[0], description: '', method: defaultPaymentMethod });
      setShowAddPaymentModal(false);
    } catch (error) {
      console.error('Error adding payment:', error);
      alert(p('error_add'));
    }
  };

  const formatPaymentMethod = (method) => getPaymentMethodLabel(method);

  // ─── Export handlers ────────────────────────────────────────────────────────

  const exportFileBasename = () =>
    `${p('export_file_basename')}-${new Date().toISOString().split('T')[0]}`;

  const exportHeaderLabels = () => getExportHeaders().map((h) => h.label);

  const handleExportCSV = () => {
    try {
      const headers = exportHeaderLabels();
      const rows = filteredPayments.map((pay) => [
        pay.client || '',
        pay.invoiceNumber || '',
        pay.totalAmount || 0,
        pay.amountPaid || 0,
        (pay.totalAmount || 0) - (pay.amountPaid || 0),
        getStatusLabel(pay.status),
        pay.nextDueDate || ''
      ]);
      const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${exportFileBasename()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV export error:', error);
      alert(p('error_export_csv'));
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const XLSX = await import('xlsx');
      const wsData = [
        exportHeaderLabels(),
        ...filteredPayments.map((pay) => [
          pay.client || '',
          pay.invoiceNumber || '',
          pay.totalAmount || 0,
          pay.amountPaid || 0,
          (pay.totalAmount || 0) - (pay.amountPaid || 0),
          getStatusLabel(pay.status),
          pay.nextDueDate || ''
        ])
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, p('title'));
      XLSX.writeFile(wb, `${exportFileBasename()}.xlsx`);
    } catch (error) {
      console.error('Excel export error:', error);
      alert(p('error_export_excel'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const paymentStatus = (pay) => getStatusLabel(pay.status);
      await printListPdfWithInvoiceTemplate({
        settings,
        rows: filteredPayments,
        columns: [
          { label: p('export_col_client'), value: (row) => row.client },
          { label: p('export_col_invoice'), value: (row) => row.invoiceNumber },
          {
            label: p('col_total_amount'),
            align: 'right',
            value: (row) => formatBrandMoney(row.totalAmount || 0),
          },
          {
            label: p('amount_paid'),
            align: 'right',
            value: (row) => formatBrandMoney(row.amountPaid || 0),
          },
          {
            label: p('remaining_to_pay'),
            align: 'right',
            value: (row) =>
              formatBrandMoney((row.totalAmount || 0) - (row.amountPaid || 0)),
          },
          { label: p('export_col_status'), value: (row) => paymentStatus(row) },
          { label: p('export_due_label'), value: (row) => row.nextDueDate },
        ],
        listExportTitle: p('export_report_title'),
        clientTitle: p('export_client_title'),
        printDocumentTitle: p('print_document_title', {
          date: new Date().toISOString().split('T')[0],
        }),
      });
    } catch (error) {
      console.error('PDF export error:', error);
      alert(p('error_export_pdf'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportJSON = () => {
    try {
      const dataStr = JSON.stringify(filteredPayments, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${exportFileBasename()}.json`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('JSON export error:', error);
      alert(p('error_export_json'));
    }
  };

  const handleExportXML = () => {
    try {
      const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<payments>
${filteredPayments.map((pay) => `  <payment>
    <client>${pay.client || ''}</client>
    <invoice>${pay.invoiceNumber || ''}</invoice>
    <totalAmount>${pay.totalAmount || 0}</totalAmount>
    <amountPaid>${pay.amountPaid || 0}</amountPaid>
    <remainingAmount>${(pay.totalAmount || 0) - (pay.amountPaid || 0)}</remainingAmount>
    <status>${getStatusLabel(pay.status)}</status>
    <nextDueDate>${pay.nextDueDate || ''}</nextDueDate>
  </payment>`).join('\n')}
</payments>`;
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${exportFileBasename()}.xml`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('XML export error:', error);
      alert(p('error_export_xml'));
    }
  };

  const handleExportHTML = () => {
    try {
      const htmlContent = `<!DOCTYPE html>
<html lang="${dateLocale.startsWith('ar') ? 'ar' : 'en'}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${p('export_report_title')}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    h1 { color: #333; }
    table { border-collapse: collapse; width: 100%; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #4CAF50; color: white; }
    tr:nth-child(even) { background-color: #f2f2f2; }
  </style>
</head>
<body>
  <h1>${p('export_report_title')}</h1>
  <p>${p('export_due_label')}: ${new Date().toLocaleString(dateLocale)}</p>
  <table>
    <thead>
      <tr>
        <th>${p('export_col_client')}</th>
        <th>${p('export_col_invoice')}</th>
        <th>${p('export_col_total')}</th>
        <th>${p('export_col_paid')}</th>
        <th>${p('export_col_remaining')}</th>
        <th>${p('export_col_status')}</th>
        <th>${p('export_col_due')}</th>
      </tr>
    </thead>
    <tbody>
${filteredPayments.map(pay => `      <tr>
        <td>${pay.client || ''}</td>
        <td>${pay.invoiceNumber || ''}</td>
        <td>${formatBrandMoney(pay.totalAmount || 0)}</td>
        <td>${formatBrandMoney(pay.amountPaid || 0)}</td>
        <td>${formatBrandMoney((pay.totalAmount || 0) - (pay.amountPaid || 0))}</td>
        <td>${getStatusLabel(pay.status)}</td>
        <td>${pay.nextDueDate || ''}</td>
      </tr>`).join('\n')}
    </tbody>
  </table>
</body>
</html>`;
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${exportFileBasename()}.html`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('HTML export error:', error);
      alert(p('error_export_html'));
    }
  };

  const handleExportTXT = () => {
    try {
      const txtContent = `${p('export_report_title')}
${p('export_due_label')}: ${new Date().toLocaleString(dateLocale)}
${'='.repeat(80)}

${filteredPayments.map(pay => `${p('export_col_client')}: ${pay.client || ''}
${p('export_col_invoice')}: ${pay.invoiceNumber || ''}
${p('export_col_total')}: ${formatBrandMoney(pay.totalAmount || 0)}
${p('export_col_paid')}: ${formatBrandMoney(pay.amountPaid || 0)}
${p('export_col_remaining')}: ${formatBrandMoney((pay.totalAmount || 0) - (pay.amountPaid || 0))}
${p('export_col_status')}: ${getStatusLabel(pay.status)}
${p('export_col_due')}: ${pay.nextDueDate || ''}
${'-'.repeat(80)}`).join('\n')}`;
      const blob = new Blob([txtContent], { type: 'text/plain' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${exportFileBasename()}.txt`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('TXT export error:', error);
      alert(p('error_export_txt'));
    }
  };

  // ─── Import handlers ────────────────────────────────────────────────────────

  const handleImportCSV = () => {
    const headers = [
      { key: 'invoiceNumber', label: p('col_invoice_number') },
      { key: 'clientName', label: p('col_client') },
      { key: 'totalAmount', label: p('col_total_amount') },
      { key: 'amountPaid', label: p('col_amount_paid') },
      { key: 'remainingAmount', label: p('col_remaining') },
      { key: 'status', label: p('col_status') },
      { key: 'nextDueDate', label: p('col_next_due') },
      { key: 'notes', label: p('description_label') }
    ];
    const schema = {
      invoiceNumber: (value) => value && value.toString().trim() !== '',
      totalAmount: (value) => !isNaN(parseFloat(value)) && parseFloat(value) >= 0,
      amountPaid: (value) => !isNaN(parseFloat(value)) && parseFloat(value) >= 0,
      status: (value) => ['pending', 'paid', 'overdue', 'partial'].includes(value) || !value
    };
    triggerFileInput(async (file) => {
      try {
        const importResults = await importFromFile(file, headers, schema);
        if (importResults.success) {
          const newPayments = importResults.data.map((payment) => {
            const matchingInvoice = documents.find(
              (doc) => (doc.type === 'facture' || doc.type === 'invoice') && doc.number === payment.invoiceNumber
            );
            if (!matchingInvoice) { console.warn(`Invoice "${payment.invoiceNumber}" not found`); return null; }
            const totalAmount = parseFloat(payment.totalAmount) || Number(matchingInvoice.amount ?? matchingInvoice.total ?? 0);
            const amountPaid = parseFloat(payment.amountPaid) || 0;
            return {
              invoiceId: matchingInvoice.id,
              invoiceNumber: payment.invoiceNumber,
              client: matchingInvoice.client,
              totalAmount,
              amountPaid,
              remainingAmount: parseFloat(payment.remainingAmount) || Math.max(0, totalAmount - amountPaid),
              status: payment.status || 'pending',
              nextDueDate: payment.nextDueDate || matchingInvoice.due_date || matchingInvoice.dueDate || matchingInvoice.date,
              installments: [],
              notes: payment.notes || '',
            };
          }).filter(Boolean);
          let successCount = 0;
          for (const payment of newPayments) {
            try {
              await createPayment(payment.invoiceId, payment);
              successCount++;
            } catch (err) {
              console.error('Payment import error:', err);
            }
          }
          await refreshData();
          alert(p('import_success', { count: successCount }));
          if (importResults.invalidCount > 0 || successCount < newPayments.length) {
            alert(p('import_partial_warning', { count: newPayments.length - successCount + importResults.invalidCount }));
          }
        } else { alert(p('import_errors', { count: importResults.errors.length })); }
      } catch (error) { alert(p('error_import')); }
    });
  };

  const handleImportExcel = () => handleImportCSV();
  const handleImportJSON = () => handleImportCSV();
  const handleImportXML = () => handleImportCSV();
  const handleImportTXT = () => handleImportCSV();

  // ─── Render ────────────────────────────────────────────────────────────────
  const conditionLabels = { on_order: p('condition_on_order'), on_delivery: p('condition_on_delivery'), on_reception: p('condition_on_reception'), end_of_month: p('condition_end_of_month'), custom_date: p('condition_custom_date') };

  const listStats = useMemo(() => [
    { key: 'pending', title: p('stat_pending'), value: stats.pendingPayments, icon: AlertCircle, color: 'from-orange-500 to-orange-600' },
    { key: 'partial', title: p('stat_partial'), value: stats.partialPayments, icon: DollarSign, color: 'from-blue-500 to-blue-600' },
    { key: 'completed', title: p('stat_completed'), value: stats.completedPayments, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { key: 'receivable', title: p('stat_total_receivable'), value: formatBrandMoney(stats.totalReceivable || 0), icon: Wallet, color: 'from-purple-500 to-purple-600' },
    { key: 'late', title: p('stat_late'), value: payments.filter((pay) => pay.status === 'late').length, icon: AlertCircle, color: 'from-red-500 to-red-600', className: 'max-lg:hidden' },
  ], [stats, payments, p]);

  const qawaemStatusOptions = useMemo(() => [
    { value: 'all', label: p('all_statuses') },
    { value: 'active', label: p('status_pending') || p('status_values.active') || p('dist_active') },
    { value: 'inactive', label: p('status_paid') || p('status_values.inactive') || p('dist_inactive') },
  ], [p]);

  const qawaemStatusLabel = statusFilter === 'all'
    ? (p('status') || p('status_label') || p('col_status'))
    : (qawaemStatusOptions.find((o) => o.value === statusFilter)?.label || p('status'));
  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'invoiceNumber', label: p('col_invoice_number'), required: true },
    { key: 'client', label: p('col_client') },
    { key: 'totalAmount', label: p('col_total_amount') },
    { key: 'amountPaid', label: p('col_amount_paid') },
    { key: 'status', label: p('col_status') },
    { key: 'nextDueDate', label: p('col_next_due') },
  ]), [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, p('col_actions')),
    [pickerColumns, p],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: 2 }),
    [qawaemBaseColumns, showCol],
  );
  return (
    <>
<FacturationListPageLayout
        footer={(
          <FacturationListPaginationBar
            totalItems={facturationListTotal}
            rawPage={facturationListPage}
            onPageChange={setFacturationListPage}
            pageSize={facturationPageSize}
          />
        )}
      className="space-y-6"
      containerClassName="employees-container"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Wallet}
      stats={listStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      columns={qawaemColumns}
      visibleColumnCount={visibleColumnCount}
      totalColumnCount={qawaemColumns.length}
      onCreate={() => setShowAddPaymentModal(true)}
      createLabel={p('new_payment')}
      headerActions={null}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import_label') || p('import') || t('common.import'),
        exportLabel: p('export_label') || t('common.export'),
        generatingLabel: p('generating') || t('common.loading'),
        isGeneratingPDF: typeof isGeneratingPDF !== 'undefined' ? isGeneratingPDF : false,
        onImportCSV: typeof handleImportCSV !== 'undefined' ? handleImportCSV : () => setShowImportDropdown(false),
        onImportExcel: typeof handleImportExcel !== 'undefined' ? handleImportExcel : () => setShowImportDropdown(false),
        onImportJSON: typeof handleImportJSON !== 'undefined' ? handleImportJSON : () => {},
        onImportXML: typeof handleImportXML !== 'undefined' ? handleImportXML : () => {},
        onImportTXT: typeof handleImportTXT !== 'undefined' ? handleImportTXT : () => {},
        onExportPDF: typeof handleExportPDF !== 'undefined' ? handleExportPDF : () => {},
        onExportExcel: typeof handleExportExcel !== 'undefined' ? handleExportExcel : () => {},
        onExportCSV: typeof handleExportCSV !== 'undefined' ? handleExportCSV : () => {},
        onExportJSON: typeof handleExportJSON !== 'undefined' ? handleExportJSON : () => {},
        onExportXML: typeof handleExportXML !== 'undefined' ? handleExportXML : () => {},
        onExportHTML: typeof handleExportHTML !== 'undefined' ? handleExportHTML : () => {},
        onExportTXT: typeof handleExportTXT !== 'undefined' ? handleExportTXT : () => {},
      }}
      showAdvancedOptions={showAdvancedOptions}
      onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
      advancedOptionsLabel={p('advanced_options')}
      advancedOptionsContent={(
<div className="space-y-4 pt-4">
          {/* Main tabs */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                >
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quick filter buttons */}
          {mainViewMode === 'all_payments' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => setQuickFilter(filter.id)}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                  >
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Distribution cards */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => (
                <button
                  key={item.id || item.name}
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.id || item.name) ? null : (item.id || item.name))}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.id || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.id || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.id || item.name) ? 'text-white/80' : 'text-white/60'}`}>
                    {(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}% du total
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      )}
      controlsStrip={(
<div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}

            {/* Right controls */}
            <div className="facturation-list-toolbar ml-auto flex shrink-0 items-center gap-2 flex-wrap">
              {/* View toggle */}
              <FacturationViewModeToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isLight={isDashboardLightMode}
              />

              {/* Status filter */}
              <div className="relative" ref={statusFilterRef}>
                <button
                  onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)}
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Filter size={18} className="text-white/80" />
                  <span className="text-white font-medium text-sm">
                    {statusFilter === 'all' ? p('status_label') : paymentStatusOptions.find(o => o.value === statusFilter)?.label || statusFilter}
                  </span>
                  <ChevronDown size={14} className={`text-white/70 transition-transform ${isStatusFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isStatusFilterOpen && (
                  <div
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px]"
                    style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}
                  >
                    <div className="p-2">
                      <button onClick={() => { setStatusFilter('all'); setIsStatusFilterOpen(false); }} className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'all' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}>
                        <Filter size={16} className="text-white/50" /><span className="text-sm font-medium">{p('all_statuses')}</span>
                      </button>
                      {paymentStatusOptions.map(opt => {
                        const OptIcon = opt.icon;
                        return (
                          <button key={opt.value} onClick={() => { setStatusFilter(opt.value); setIsStatusFilterOpen(false); }} className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === opt.value ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}>
                            <OptIcon size={16} className={opt.style.split(' ')[1]} /><span className="text-sm font-medium">{opt.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Date range picker */}
              <div className="relative" ref={dateRangeRef}>
                <button
                  onClick={() => { setIsDateRangeOpen(!isDateRangeOpen); setShowImportDropdown(false); setShowExportDropdown(false); }}
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Calendar size={18} className="text-white/80" /><span className="text-white font-medium text-sm">{p('period')}</span><ChevronDown size={14} className={`text-white/70 transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDateRangeOpen && (
                  <div
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[320px]"
                    style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}
                  >
                    <div className="p-4" style={{ overflow: 'visible' }}>
                      <div className="flex flex-col gap-3" style={{ overflow: 'visible' }}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ overflow: 'visible' }}>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('start_date')}</label>
                            <DatePicker
                              selected={dateRange.start}
                              onChange={(date) => setDateRange({ ...dateRange, start: date })}
                              dateFormat="dd/MM/yyyy"
                              placeholderText={p('select_placeholder')}
                              locale={datePickerLocale}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm gps-datepicker"
                              calendarClassName="gps-calendar"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                              popperProps={{ strategy: 'fixed' }}
                              renderCustomHeader={({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem 1rem', gap: '1rem' }}>
                                  <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled} type="button" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem', borderRadius: '0.5rem', transition: 'all 0.2s', color: 'rgba(255,255,255,0.8)', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseEnter={(e) => e.target.style.background = 'rgba(59,130,246,0.3)'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                                    <ChevronDown style={{ transform: 'rotate(90deg)' }} size={16} />
                                  </button>
                                  <div style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>{date.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })}</div>
                                  <button onClick={increaseMonth} disabled={nextMonthButtonDisabled} type="button" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem', borderRadius: '0.5rem', transition: 'all 0.2s', color: 'rgba(255,255,255,0.8)', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseEnter={(e) => e.target.style.background = 'rgba(59,130,246,0.3)'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                                    <ChevronDown style={{ transform: 'rotate(-90deg)' }} size={16} />
                                  </button>
                                </div>
                              )}
                            />
                          </div>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('end_date')}</label>
                            <DatePicker
                              selected={dateRange.end}
                              onChange={(date) => setDateRange({ ...dateRange, end: date })}
                              dateFormat="dd/MM/yyyy"
                              placeholderText={p('select_placeholder')}
                              minDate={dateRange.start}
                              locale={datePickerLocale}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm gps-datepicker"
                              calendarClassName="gps-calendar"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                              popperProps={{ strategy: 'fixed' }}
                              renderCustomHeader={({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem 1rem', gap: '1rem' }}>
                                  <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled} type="button" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem', borderRadius: '0.5rem', transition: 'all 0.2s', color: 'rgba(255,255,255,0.8)', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseEnter={(e) => e.target.style.background = 'rgba(59,130,246,0.3)'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                                    <ChevronDown style={{ transform: 'rotate(90deg)' }} size={16} />
                                  </button>
                                  <div style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>{date.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })}</div>
                                  <button onClick={increaseMonth} disabled={nextMonthButtonDisabled} type="button" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem', borderRadius: '0.5rem', transition: 'all 0.2s', color: 'rgba(255,255,255,0.8)', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseEnter={(e) => e.target.style.background = 'rgba(59,130,246,0.3)'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                                    <ChevronDown style={{ transform: 'rotate(-90deg)' }} size={16} />
                                  </button>
                                </div>
                              )}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2 border-t border-blue-400/30">
                          <button onClick={() => setDateRange({ start: null, end: null })} className="flex-1 px-3 py-2 bg-white/5 hover:bg-blue-500/20 text-white/80 hover:text-white rounded-xl text-sm transition-all font-medium">{p('reset')}</button>
                          <button onClick={() => setIsDateRangeOpen(false)} className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl text-sm transition-all font-medium shadow-lg shadow-blue-500/30">{p('apply')}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Bulk delete */}
              {selectedPayments.length > 0 && (
                <button
                  type="button"
                  title={p('delete_selection')}
                  onClick={handleBulkDeletePayments}
                  className="flex items-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 rounded-lg transition-all text-sm font-medium"
                >
                  <Trash2 size={16} /><span className="hidden sm:inline">{p('delete_count', { count: selectedPayments.length })}</span>
                </button>
              )}

              {/* Import */}
              <div className="relative z-[100001]" ref={importDropdownRef}>
                <button onClick={() => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); }} className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5">
                  <Upload size={16} /><span className="text-sm font-medium">{p('import_label')}</span><ChevronDown size={14} className={`transition-transform ${showImportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showImportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[140px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}>
                    <div className="p-2">
                      <button onClick={() => { handleImportCSV(); setShowImportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-red-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                      <button onClick={() => { handleImportExcel(); setShowImportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_xlsx')}</span></button>
                      <button onClick={() => { handleImportJSON(); setShowImportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileOutput size={16} className="text-blue-400" /><span className="text-sm font-medium">{t('common.format_json')}</span></button>
                      <button onClick={() => { handleImportXML(); setShowImportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-orange-400" /><span className="text-sm font-medium">{t('common.format_xml')}</span></button>
                      <button onClick={() => { handleImportTXT(); setShowImportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-gray-400" /><span className="text-sm font-medium">{t('common.format_txt_tsv')}</span></button>
                    </div>
                  </div>
                )}
              </div>

              {/* Export */}
              <div className="relative z-[100001]" ref={exportDropdownRef}>
                <button onClick={() => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); }} className="bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5">
                  <Download size={16} /><span className="text-sm font-medium">{p('export_label')}</span><ChevronDown size={14} className={`transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[140px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}>
                    <div className="p-2">
                      <button onClick={() => { handleExportPDF(); setShowExportDropdown(false); }} disabled={isGeneratingPDF} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"><FileText size={16} className="text-blue-400" /><span className="text-sm font-medium">{isGeneratingPDF ? p('generating_pdf') : t('common.format_pdf')}</span></button>
                      <button onClick={() => { handleExportExcel(); setShowExportDropdown(false); }} disabled={isGeneratingExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{isGeneratingExcel ? p('generating_excel') : t('common.format_xlsx')}</span></button>
                      <button onClick={() => { handleExportCSV(); setShowExportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileOutput size={16} className="text-purple-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                      <button onClick={() => { handleExportJSON(); setShowExportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileOutput size={16} className="text-blue-300" /><span className="text-sm font-medium">{t('common.format_json')}</span></button>
                      <button onClick={() => { handleExportXML(); setShowExportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-orange-400" /><span className="text-sm font-medium">{t('common.format_xml')}</span></button>
                      <button onClick={() => { handleExportHTML(); setShowExportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-red-400" /><span className="text-sm font-medium">{t('common.format_html')}</span></button>
                      <button onClick={() => { handleExportTXT(); setShowExportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-gray-400" /><span className="text-sm font-medium">{t('common.format_txt')}</span></button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      filterActions={selectedPayments.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDeletePayments}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
        >
          <Trash2 size={16} />
          <span>{p('delete_count', { count: selectedPayments.length })}</span>
        </button>
      ) : null}
    >
        {/* ── Table View ── */}
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <th className="px-2 py-4 text-center text-white font-semibold" style={{ width: 32 }}></th>
                  <th className="px-3 py-3 text-center align-middle" style={{ width: 36 }}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {showCol('invoiceNumber') ? <TableColumnHeaderFilter columnKey="invoiceNumber" label={p('col_invoice_number')} value={filters.invoiceNumber || ''} onChange={(v) => setFilter('invoiceNumber', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('client') ? <TableColumnHeaderFilter columnKey="client" label={p('col_client')} value={filters.client || ''} onChange={(v) => setFilter('client', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('totalAmount') ? <TableColumnHeaderFilter columnKey="totalAmount" label={p('col_total_amount')} value={filters.totalAmount || ''} onChange={(v) => setFilter('totalAmount', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('amountPaid') ? <TableColumnHeaderFilter columnKey="amountPaid" label={p('col_amount_paid')} value={filters.amountPaid || ''} onChange={(v) => setFilter('amountPaid', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" /> : null}
                  <th className="px-3 py-4 text-center text-white font-semibold">{p('col_remaining')}</th>
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={[{ value: 'pending', label: p('status_pending') }, { value: 'partial', label: p('status_partial') }, { value: 'paid', label: p('status_paid') }, { value: 'late', label: p('status_late') }]} value={filters.status || ''} onChange={(v) => setFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('nextDueDate') ? <TableColumnHeaderFilter columnKey="nextDueDate" label={p('col_next_due')} value={filters.nextDueDate || ''} onChange={(v) => setFilter('nextDueDate', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" /> : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                    thClassName="px-3 py-4 text-center text-white font-semibold align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {loading ? (
                  <tr>
                    <td colSpan={visibleTableColCount} className="px-4 py-8 text-center text-white/60">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 bg-blue-600 rounded-full animate-pulse"></div>
                        <span>{t('common.loading')}</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredPayments.length === 0 ? (
                  <tr>
                    <td colSpan={visibleTableColCount} className="text-center py-12 text-white/60">
                      <Wallet className="h-12 w-12 text-white/30 mx-auto mb-4" />
                      <p className="text-lg">{p('empty')}</p>
                    </td>
                  </tr>
                ) : (
                  pagedItems.map((payment, index) => {
                    const isExpanded = expandedRows.has(payment.id);
                    const hasInstallments = payment.installments && payment.installments.length > 0;
                    return (
                      <React.Fragment key={payment.id}>
                        <tr
                          className={`hover:bg-white/5 transition-colors ${selectedPayments.includes(payment.id) ? 'bg-white/10' : ''}`}
                        >
                          {/* Expand */}
                          <td className="px-2 py-3 text-center">
                            {hasInstallments ? (
                              <button
                                onClick={() => setExpandedRows(prev => { const next = new Set(prev); if (next.has(payment.id)) next.delete(payment.id); else next.add(payment.id); return next; })}
                                className="p-1 text-blue-400 hover:text-blue-300 transition-colors"
                                title={isExpanded ? p('collapse') : p('expand_installments')}
                              >
                                <ChevronRight size={16} style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                              </button>
                            ) : <span className="w-6 inline-block" />}
                          </td>
                          {/* Checkbox */}
                          <td className="px-3 py-3 text-center align-middle">
                            <div className="flex justify-center">
                              <QawaemTableCheckbox
                                checked={selectedPayments.includes(payment.id)}
                                onChange={() => handleSelectPayment(payment.id)}
                              />
                            </div>
                          </td>
                          {showCol('invoiceNumber') ? (
                            <td className="px-3 py-3">
                              <PaymentEditableCell recordId={payment.id} field="invoiceNumber" value={payment.invoiceNumber} className="text-white font-semibold"
                                isEditing={editingCell?.recordId === payment.id && editingCell?.field === 'invoiceNumber'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </td>
                          ) : null}
                          {showCol('client') ? (
                            <td className="px-3 py-3">
                              <PaymentEditableCell recordId={payment.id} field="client" value={payment.client} className="text-white font-medium"
                                isEditing={editingCell?.recordId === payment.id && editingCell?.field === 'client'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </td>
                          ) : null}
                          {showCol('totalAmount') ? (
                            <td className="px-3 py-3 text-center text-white font-medium">{formatBrandMoney(payment.totalAmount || 0)}</td>
                          ) : null}
                          {showCol('amountPaid') ? (
                            <td className="px-3 py-3 text-center text-green-400 font-medium">{formatBrandMoney(payment.amountPaid || 0)}</td>
                          ) : null}
                          <td className="px-3 py-3 text-center text-orange-400 font-medium">{formatBrandMoney((payment.totalAmount || 0) - (payment.amountPaid || 0))}</td>
                          {showCol('status') ? (
                            <td className="px-3 py-3 text-center">
                              <div className="flex justify-center">
                                <QawaemClickableStatusBadge status={payment.status} recordId={payment.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                              </div>
                            </td>
                          ) : null}
                          {showCol('nextDueDate') ? (
                            <td className="px-3 py-3 text-center text-white/80">{payment.nextDueDate || '-'}</td>
                          ) : null}
                          <td className="px-3 py-3 text-center">
                            <QawaemErpRowActions
                              onView={() => handleQuickAction(payment, 'view')}
                              onEdit={() => handleQuickAction(payment, 'addPayment')}
                              onDelete={() => handleDeletePayment(payment.id)}
                              viewTitle={p('view_details')}
                              editTitle={p('add_payment_action')}
                              deleteTitle={t('common.delete')}
                            />
                          </td>
                        </tr>

                        {/* Expandable installments */}
                        {isExpanded && hasInstallments && (
                          <tr key={`${payment.id}-expanded`}>
                            <td colSpan={visibleTableColCount} style={{ padding: '0 0 0.5rem 0', background: 'rgba(59,130,246,0.03)' }}>
                              <div style={{ marginLeft: '2.5rem', marginRight: '1rem', borderLeft: '2px solid rgba(59,130,246,0.35)', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ background: 'rgba(59,130,246,0.08)' }}>
                                      {[p('installment_tranche'), p('installment_percent'), p('installment_amount'), p('installment_due_date'), p('installment_condition'), p('installment_status'), p('installment_action')].map(h => (
                                        <th key={h} style={{ padding: '0.4rem 0.75rem', textAlign: h === p('installment_tranche') ? 'left' : 'center', fontSize: '0.7rem', color: 'rgba(147,197,253,0.8)', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {payment.installments.map((inst, idx) => (
                                      <tr key={inst.id || idx} style={{ borderTop: '1px solid rgba(255,255,255,0.05)', background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                                        <td style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', fontWeight: '500' }}>{p('installment_number', { n: idx + 1 })}</td>
                                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>{inst.percentage ? `${inst.percentage}%` : '—'}</td>
                                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontSize: '0.8rem', color: '#ffffff', fontWeight: '600' }}>{formatBrandMoney(parseFloat(inst.amount) || 0)}</td>
                                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>{inst.dueDate || '—'}</td>
                                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>{conditionLabels[inst.condition] || inst.condition || '—'}</td>
                                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: '600', background: inst.status === 'paid' ? 'rgba(34,197,94,0.15)' : 'rgba(251,191,36,0.15)', color: inst.status === 'paid' ? '#4ade80' : '#fbbf24', border: inst.status === 'paid' ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(251,191,36,0.3)' }}>
                                            {inst.status === 'paid' ? p('status_collected') : p('status_waiting')}
                                          </span>
                                        </td>
                                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                                          {inst.status !== 'paid' ? (
                                            <button onClick={() => handleMarkAsPaid(payment.id, inst.id)} className="p-1.5 text-green-400 hover:text-green-300 transition-colors" title={p('mark_collected')}>
                                              <CheckCircle size={15} />
                                            </button>
                                          ) : <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem' }}>—</span>}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_payments_count', { count: filteredPayments.length })}</span>
                      <span>{p('footer_total_paid')}: <span className="text-white font-semibold">{formatBrandMoney(filteredPayments.reduce((s, p) => s + (p.amountPaid || 0), 0))}</span></span>
                      <span>{p('remaining_to_pay')}: <span className="text-white font-semibold">{formatBrandMoney(filteredPayments.reduce((s, p) => s + ((p.totalAmount || 0) - (p.amountPaid || 0)), 0))}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* ── Card View ── */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {filteredPayments.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Wallet size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            ) : pagedItems.map((payment, index) => (
              <motion.div
                key={payment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04 }}
                className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Col 1: Informations Client */}
                  <div>
                    <h5 className="font-medium text-white/60 text-xs uppercase tracking-wider mb-3">{p('section_client_info')}</h5>
                    <div className="space-y-2">
                      <p className="text-white font-semibold text-lg">{payment.client || '-'}</p>
                      <p className="text-white/70 text-sm">{payment.invoiceNumber || '-'}</p>
                      <div className="mt-2"><QawaemClickableStatusBadge status={payment.status} recordId={payment.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                    </div>
                  </div>
                  {/* Col 2: Montants */}
                  <div>
                    <h5 className="font-medium text-white/60 text-xs uppercase tracking-wider mb-3">{p('section_amounts')}</h5>
                    <div className="space-y-2">
                      <div><p className="text-white/50 text-xs">{p('col_total_amount')}</p><p className="text-white font-semibold">{formatBrandMoney(payment.totalAmount || 0)}</p></div>
                      <div><p className="text-white/50 text-xs">{p('paid_label')}</p><p className="text-green-400 font-semibold">{formatBrandMoney(payment.amountPaid || 0)}</p></div>
                      <div><p className="text-white/50 text-xs">{p('col_remaining')}</p><p className="text-orange-400 font-semibold">{formatBrandMoney((payment.totalAmount || 0) - (payment.amountPaid || 0))}</p></div>
                    </div>
                  </div>
                  {/* Col 3: Échéancier */}
                  <div>
                    <h5 className="font-medium text-white/60 text-xs uppercase tracking-wider mb-3">{p('schedule_title', { count: (payment.installments || []).length })}</h5>
                    <div className="space-y-1.5">
                      {(payment.installments || []).slice(0, 3).map((inst, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-white/60">{p('installment_short', { n: idx + 1 })} — {inst.dueDate || '?'}</span>
                          <span className={`px-2 py-0.5 rounded-full font-medium ${inst.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                            {formatBrandMoney(parseFloat(inst.amount) || 0)}
                          </span>
                        </div>
                      ))}
                      {(payment.installments || []).length > 3 && (
                        <p className="text-white/40 text-xs">{p('more_installments', { count: payment.installments.length - 3 })}</p>
                      )}
                    </div>
                  </div>
                  {/* Col 4: Prochaine Échéance & Actions */}
                  <div className="flex flex-col justify-between">
                    <div>
                      <h5 className="font-medium text-white/60 text-xs uppercase tracking-wider mb-3">{p('next_due_title')}</h5>
                      <p className="text-white font-medium">{payment.nextDueDate || '-'}</p>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleQuickAction(payment, 'view')}
                      onEdit={() => handleQuickAction(payment, 'addPayment')}
                      onDelete={() => handleDeletePayment(payment.id)}
                      viewTitle={p('view_details')}
                      editTitle={p('add_payment_action')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </FacturationListPageLayout>

      {/* ── View Modal (portal) ── */}
      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
          open
          onClose={() => setShowViewModal(false)}
          title={p('view_modal_title')}
          icon={Wallet}
          maxWidth="1100px"
          testId="payment-view-modal"
        >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/20"><p className="text-white/60 text-sm mb-1">{p('col_client')}</p><p className="text-white font-semibold">{viewingRecord.client}</p></div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/20"><p className="text-white/60 text-sm mb-1">{p('col_invoice_number')}</p><p className="text-white font-semibold">{viewingRecord.invoiceNumber}</p></div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/20"><p className="text-white/60 text-sm mb-1">{p('col_status')}</p><div className="mt-1"><QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div></div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2"><DollarSign size={16} className="text-green-400" />{p('financial_summary')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white/5 rounded-lg p-4"><p className="text-white/60 text-sm mb-1">{p('col_total_amount')}</p><p className="text-2xl font-bold text-white">{formatBrandMoney(viewingRecord.totalAmount || 0)}</p></div>
                    <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/20"><p className="text-white/60 text-sm mb-1">{p('amount_paid')}</p><p className="text-2xl font-bold text-green-400">{formatBrandMoney(viewingRecord.amountPaid || 0)}</p></div>
                    <div className="bg-orange-500/10 rounded-lg p-4 border border-orange-500/20"><p className="text-white/60 text-sm mb-1">{p('remaining_to_pay')}</p><p className="text-2xl font-bold text-orange-400">{formatBrandMoney((viewingRecord.totalAmount || 0) - (viewingRecord.amountPaid || 0))}</p></div>
                  </div>
                </div>
                {viewingRecord.installments && viewingRecord.installments.length > 0 && (
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h4 className="text-white font-semibold mb-4 flex items-center gap-2"><Calendar size={16} className="text-blue-400" />{p('payment_installments')}</h4>
                    <div className="space-y-3">
                      {viewingRecord.installments.map((inst, idx) => (
                        <div key={inst.id || idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                          <div>
                            <p className="text-white font-medium">{p('installment_number', { n: idx + 1 })}</p>
                            <p className="text-white/60 text-sm">Échéance: {inst.dueDate || p('undefined_due')}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-semibold">{formatBrandMoney(parseFloat(inst.amount) || 0)}</p>
                            <span className={`text-xs px-2 py-1 rounded-full ${inst.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                              {inst.status === 'paid' ? p('status_paid') : p('status_pending')}
                            </span>
                          </div>
                          {inst.status !== 'paid' && (
                            <button onClick={() => handleMarkAsPaid(viewingRecord.id, inst.id)} className="ml-3 p-2 rounded-lg bg-green-600/20 hover:bg-green-600/30 text-green-400 transition-colors" title={p('mark_paid')}>
                              <CheckCircle size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
        </QawaemErpViewModal>
      ) : null}

      {showAddPaymentModal && (
        <QawaemErpCrudModal
          open={showAddPaymentModal}
          onClose={() => setShowAddPaymentModal(false)}
          title={p('modal_add_title')}
          icon={Wallet}
          formId="payment-add-form"
          saveLabel={p('submit_add_payment')}
          onSubmit
          maxWidth="480px"
          testId="payment-add-modal"
        >
            <form id="payment-add-form" onSubmit={handlePaymentSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block w-full text-white/70 text-sm font-medium mb-2 text-right">{p('invoice_label')}</label>
                <CustomSelect
                  value={paymentFormData.invoiceId}
                  onChange={(value) => setPaymentFormData(prev => ({ ...prev, invoiceId: value }))}
                  options={[
                    { value: '', label: p('select_invoice') },
                    ...payments.filter(p => p.status !== 'paid').map(payment => ({
                      value: payment.invoiceId,
                      label: p('invoice_remaining', {
                        number: payment.invoiceNumber,
                        client: payment.client,
                        amount: formatBrandMoney(payment.remainingAmount || 0),
                      })
                    }))
                  ]}
                  placeholder={p('select_invoice')}
                  required
                />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm font-medium mb-2 text-right">{p('amount_label')}</label>
                <CustomNumberInput value={paymentFormData.amount} onChange={(value) => setPaymentFormData(prev => ({ ...prev, amount: value }))} placeholder="0.00" step={0.01} decimals={2} required />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm font-medium mb-2 text-right">{p('payment_date_label')}</label>
                <CustomDatePicker value={paymentFormData.paymentDate} onChange={(value) => setPaymentFormData(prev => ({ ...prev, paymentDate: value }))} placeholder={p('select_date_ph')} required />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm font-medium mb-2 text-right">{p('payment_method_label')}</label>
                <CustomSelect
                  value={paymentFormData.method}
                  onChange={(value) => setPaymentFormData(prev => ({ ...prev, method: value }))}
                  options={paymentMethodOptions}
                  placeholder={p('payment_method_label')}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block w-full text-white/70 text-sm font-medium mb-2 text-right">{p('description_label')}</label>
                <textarea
                  value={paymentFormData.description}
                  onChange={(e) => setPaymentFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={p('description_ph')}
                  rows="3"
                />
              </div>
              <div className="md:col-span-3 flex space-x-4 pt-2">
                <button type="button" onClick={() => setShowAddPaymentModal(false)} className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all duration-300">{t('common.cancel')}</button>
              </div>
            </form>
        </QawaemErpCrudModal>
      )}

      {showPaymentModal && selectedPayment ? (
        <QawaemErpViewModal
          open
          onClose={() => setShowPaymentModal(false)}
          title={p('details_modal_title')}
          icon={Wallet}
          maxWidth="720px"
          testId="payment-details-modal"
        >
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-4"><p className="text-white/60 text-sm">{p('col_client')}</p><p className="text-white font-semibold">{selectedPayment.client}</p></div>
                <div className="bg-white/5 rounded-xl p-4"><p className="text-white/60 text-sm">{p('col_invoice_number')}</p><p className="text-white font-semibold">{selectedPayment.invoiceNumber}</p></div>
                <div className="bg-white/5 rounded-xl p-4"><p className="text-white/60 text-sm">{p('col_status')}</p><div className="mt-1"><QawaemClickableStatusBadge status={selectedPayment.status} recordId={selectedPayment.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div></div>
              </div>
              <div className="bg-white/5 rounded-xl p-6">
                <h4 className="text-white font-semibold mb-4">{p('financial_summary')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-white/60 text-sm">{p('col_total_amount')}</p><p className="text-2xl font-bold text-white">{formatBrandMoney(selectedPayment.totalAmount || 0)}</p></div>
                  <div><p className="text-white/60 text-sm">{p('amount_paid')}</p><p className="text-2xl font-bold text-green-400">{formatBrandMoney(selectedPayment.amountPaid || 0)}</p></div>
                  <div><p className="text-white/60 text-sm">{p('remaining_to_pay')}</p><p className="text-2xl font-bold text-orange-400">{formatBrandMoney((selectedPayment.totalAmount || 0) - (selectedPayment.amountPaid || 0))}</p></div>
                </div>
              </div>
              {selectedPayment.installments && selectedPayment.installments.length > 0 && (
                <div className="bg-white/5 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-4">{p('payment_installments')}</h4>
                  <div className="space-y-3">
                    {selectedPayment.installments.map((installment, index) => (
                      <div key={installment.id || index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{p('installment_number', { n: installment.number || index + 1 })}</p>
                          <p className="text-white/60 text-sm">{p('due_date_label')}: {installment.dueDate || p('undefined_due')}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-semibold">{formatBrandMoney(parseFloat(installment.amount) || 0)}</p>
                          <span className={`text-xs px-2 py-1 rounded-full ${installment.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                            {installment.status === 'paid' ? p('status_paid') : p('status_pending')}
                          </span>
                        </div>
                        {installment.status !== 'paid' && (
                          <button onClick={() => handleMarkAsPaid(selectedPayment.id, installment.id)} className="ml-3 p-2 rounded-lg bg-green-600/20 hover:bg-green-600/30 text-green-400 transition-colors" title={p('mark_paid')}>
                            <CheckCircle size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
        </QawaemErpViewModal>
      ) : null}

    </>
  );
};

export default Payments;


